import React, { useState, useEffect } from 'react';
import { Mic, Mail, Globe, Users, BookOpen, Award, ArrowRight, Send, Loader2, CheckCircle, AlertCircle, Calendar, MapPin, Download } from 'lucide-react';
import { Link } from 'react-router-dom';
import SEOMeta, { PAGE_META } from '../components/SEOMeta';
import { SpeakingServiceSchema, PersonSchema } from '../components/JsonLdSchema';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const generateICS = (event) => {
  const start = event.event_date ? event.event_date.replace(/-/g, '') + 'T090000Z' : '';
  const end = event.event_date ? event.event_date.replace(/-/g, '') + 'T170000Z' : '';
  const ics = `BEGIN:VCALENDAR\nVERSION:2.0\nBEGIN:VEVENT\nDTSTART:${start}\nDTEND:${end}\nSUMMARY:${event.title}\nLOCATION:${event.location || 'TBC'}\nDESCRIPTION:${event.description || ''}\nEND:VEVENT\nEND:VCALENDAR`;
  const blob = new Blob([ics], { type: 'text/calendar' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = `${event.title.replace(/\s+/g, '-')}.ics`; a.click();
  URL.revokeObjectURL(url);
};

const Speaking = () => {
  const [formData, setFormData] = useState({ name: '', email: '', organisation: '', event_type: '', event_date: '', audience_size: '', phone: '', message: '' });
  const [status, setStatus] = useState('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [events, setEvents] = useState([]);
  const [pastEvents, setPastEvents] = useState([]);

  useEffect(() => {
    fetch(`${API_URL}/api/public/events?status=upcoming`).then(r => r.json()).then(d => setEvents(d.events || [])).catch(() => {});
    fetch(`${API_URL}/api/public/events?status=past`).then(r => r.json()).then(d => setPastEvents(d.events || [])).catch(() => {});
  }, []);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    setErrorMsg('');
    try {
      const res = await fetch(`${API_URL}/api/email/speaking-enquiry`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(formData) });
      if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.detail || 'Failed to send enquiry'); }
      setStatus('success');
    } catch (err) { setStatus('error'); setErrorMsg(err.message || 'Something went wrong.'); }
  };

  const topics = [
    { title: 'Third Culture Leadership', desc: 'Leading across cultural difference without losing yourself or forcing others to lose themselves.', format: 'Keynote / Workshop' },
    { title: 'Cultural Fluency Without Compromise', desc: 'Practical frameworks for multicultural teams, organisations, and communities.', format: 'Keynote / Full-day Workshop' },
    { title: 'The CFQ Framework', desc: 'Self-Anchoring, Perspective-Shifting, Adaptive Communication, and Relational Integration.', format: 'Workshop / Executive Retreat' },
    { title: 'Leadership Identity in Multicultural Contexts', desc: 'Remaining internally stable while externally flexible across cultural complexity.', format: 'Keynote' },
  ];

  const audiences = ['Executive leadership teams', 'Healthcare professionals', 'Global organisations', 'Educational institutions', 'Faith-based communities', 'Professional associations'];

  const inputClass = "w-full bg-white border border-[#071B3A]/14 px-4 py-3.5 text-base text-[#071B3A] placeholder-[#071B3A]/30 focus:outline-none focus:ring-1 focus:ring-[#C99316]/40 focus:border-[#C99316]/50 transition-all disabled:opacity-50";

  return (
    <div className="bg-[#FAF8F3]" data-testid="speaking-page">
      <SEOMeta url="/speaking" {...PAGE_META.speaking} />
      <SpeakingServiceSchema />
      <PersonSchema />

      {/* ═══ HERO ═══ */}
      <section className="relative pt-36 pb-24 lg:pb-28 overflow-hidden bg-[#071B3A]">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 right-0 w-[60%] h-full bg-gradient-to-l from-[#C99316]/[0.02] to-transparent" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <span className="block text-[11px] tracking-[0.25em] uppercase text-[#C99316] font-sans font-semibold mb-6">Speaking &amp; Workshops</span>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-[72px] text-[#FAF8F3] font-light tracking-tighter leading-[0.92] mb-6">
            Speaking &amp; <span className="text-[#C99316]">Workshops</span>
          </h1>
          <p className="text-[#FAF8F3]/80 text-lg font-light leading-[1.8] tracking-wide max-w-xl mb-10">
            Invite Dr Promise Monday to your next leadership event, conference, or executive retreat.
          </p>
          <a
            href="#enquiry-form"
            data-testid="speaking-hero-cta"
            className="inline-flex items-center gap-3 px-8 py-4 font-sans text-sm uppercase tracking-[0.12em] font-medium transition-all duration-300 hover:bg-[#C99316] hover:text-[#071B3A]"
            style={{ border: '1px solid rgba(201,147,22,0.7)', color: '#C99316' }}
          >
            <span>Enquire About Speaking</span> <ArrowRight size={16} />
          </a>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#C99316]/20 to-transparent" />
      </section>

      {/* ═══ FOUNDER BIO ═══ */}
      <section className="py-16 lg:py-20" style={{ backgroundColor: '#FAF8F3' }}>
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <div className="flex flex-col md:flex-row items-start gap-10 lg:gap-16">
            {/* Portrait */}
            <div className="flex-shrink-0 mx-auto md:mx-0">
              <div
                className="relative overflow-hidden"
                style={{ width: '200px', height: '240px', borderRadius: '2px' }}
              >
                <img
                  src="https://customer-assets-jai6qajn.emergentagent.net/job_cd729cf5-4469-4028-b6ba-a9c891131dbe/artifacts/dnctg08g_1780602847089.jpg"
                  alt="Dr Promise Monday"
                  className="w-full h-full object-cover object-top"
                />
                {/* Gold accent bar at bottom of portrait */}
                <div
                  className="absolute bottom-0 left-0 right-0"
                  style={{ height: '3px', backgroundColor: '#C99316', opacity: 0.7 }}
                />
              </div>
            </div>

            {/* Bio text */}
            <div className="flex-1 pt-2">
              <p className="text-[11px] uppercase tracking-[0.3em] text-[#C99316]/80 font-sans mb-4">The Speaker</p>
              <h2 className="font-serif text-2xl sm:text-3xl text-[#071B3A] font-light mb-1">Dr Promise Monday</h2>
              <p className="text-[11px] uppercase tracking-[0.18em] text-[#071B3A]/35 font-sans mb-6">
                Paediatrician &bull; Author &bull; Founder, Third Culture Leadership
              </p>
              <div
                className="mb-6"
                style={{ width: '40px', height: '1px', backgroundColor: '#C99316', opacity: 0.45 }}
              />
              <p className="text-[#071B3A]/75 text-base leading-relaxed mb-4 max-w-xl">
                Dr Promise Monday is a Paediatrician, author, and the founder of Third Culture Leadership.
                Drawing on professional experience across multiple cultural systems, he speaks and writes
                at the intersection of cultural intelligence and practical leadership.
              </p>
              <p className="text-[#071B3A]/70 text-base leading-relaxed max-w-xl">
                His CFQ Framework — built around Self-Anchoring, Perspective-Shifting, Adaptive
                Communication, and Relational Integration — has equipped leaders in healthcare,
                education, and teams navigating cultural difference to lead with clarity across difference.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to="/about"
                  className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-semibold text-[#071B3A]/50 hover:text-[#071B3A] transition-colors duration-200"
                  data-testid="speaking-bio-about-link"
                >
                  Full biography <ArrowRight size={13} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ TOPICS ═══ */}
      <section className="py-20 lg:py-24" style={{ backgroundColor: '#FAF8F3' }}>
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <div className="mb-14">
            <p className="text-[11px] uppercase tracking-[0.3em] text-[#C99316]/80 font-sans mb-4">Signature Topics</p>
            <h2 className="font-serif text-3xl sm:text-4xl text-[#071B3A] font-light">Speaking Topics</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="topics-grid">
            {topics.map((t, i) => (
              <div key={i} className="bg-white p-6 transition-all duration-300 hover:border-[#C99316]/25" style={{ border: '1px solid rgba(7,27,58,0.08)' }} data-testid={`topic-card-${i}`}>
                <h3 className="font-serif text-lg text-[#071B3A] font-semibold mb-2">{t.title}</h3>
                <p className="text-[#071B3A]/70 text-base leading-relaxed mb-3">{t.desc}</p>
                <p className="text-[#C99316]/80 text-xs uppercase tracking-wider">{t.format}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ AUDIENCES ═══ */}
      <section className="bg-[#071B3A] py-16">
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <div className="text-center mb-10">
            <p className="text-[11px] uppercase tracking-[0.3em] text-[#C99316]/80 font-sans mb-4">Ideal For</p>
            <h2 className="font-serif text-2xl sm:text-3xl text-[#FAF8F3] font-light">Audiences &amp; Contexts</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="audiences-grid">
            {audiences.map((a, i) => (
              <div key={i} className="flex items-center gap-3 bg-white p-4 transition-all" style={{ border: '1px solid rgba(7,27,58,0.08)' }}>
                <Users size={16} className="flex-shrink-0" style={{ color: 'rgba(201,147,22,0.6)' }} />
                <span className="text-[#071B3A]/65 text-sm">{a}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="mt-16 h-px" style={{ background: 'linear-gradient(to right, transparent, rgba(201,147,22,0.15), transparent)' }} />
      </section>

      {/* ═══ FORMATS ═══ */}
      <section className="py-16" style={{ backgroundColor: '#EDE8DC' }}>
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <div className="mb-10">
            <p className="text-[11px] uppercase tracking-[0.3em] text-[#C99316]/80 font-sans mb-4">Formats</p>
            <h2 className="font-serif text-2xl sm:text-3xl text-[#071B3A] font-light">Engagement Options</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="formats-grid">
            {[
              { Icon: Award, title: 'Keynote Address', desc: '45-60 minute signature talks for conferences and events' },
              { Icon: BookOpen, title: 'Workshops', desc: 'Half-day or full-day interactive sessions with hands-on activities' },
              { Icon: Globe, title: 'Executive Retreats', desc: 'Customised programmes tailored for leadership teams' },
            ].map((f, i) => (
              <div key={i} className="bg-white p-6 text-center transition-all" style={{ border: '1px solid rgba(7,27,58,0.08)' }} data-testid={`format-card-${i}`}>
                <div className="inline-flex items-center justify-center w-12 h-12 mb-4" style={{ backgroundColor: 'rgba(201,147,22,0.08)' }}>
                  <f.Icon size={22} style={{ color: 'rgba(201,147,22,0.7)' }} />
                </div>
                <h3 className="text-[#071B3A] font-semibold text-sm mb-2">{f.title}</h3>
                <p className="text-[#071B3A]/70 text-sm leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ UPCOMING EVENTS ═══ */}
      {events.length > 0 && (
        <section className="py-16" style={{ backgroundColor: '#FAF8F3' }}>
          <div className="max-w-4xl mx-auto px-5 sm:px-8">
            <div className="mb-10">
              <p className="text-[11px] uppercase tracking-[0.3em] text-[#C99316]/60 font-sans mb-4">Upcoming</p>
              <h2 className="font-serif text-2xl sm:text-3xl text-[#071B3A] font-light">Events &amp; Appearances</h2>
            </div>
            <div className="space-y-3" data-testid="events-list">
              {events.map((ev) => (
                <div key={ev.id} className="bg-white p-5 flex flex-col sm:flex-row sm:items-center gap-4 transition-all" style={{ border: '1px solid rgba(7,27,58,0.08)' }}>
                  <div className="w-14 h-14 flex flex-col items-center justify-center flex-shrink-0" style={{ backgroundColor: '#071B3A' }}>
                    <span className="text-[#C99316] text-lg font-serif font-bold leading-none">{ev.event_date ? new Date(ev.event_date).getDate() : '-'}</span>
                    <span className="text-[#FAF8F3]/40 text-[9px] uppercase">{ev.event_date ? new Date(ev.event_date).toLocaleDateString('en-GB', { month: 'short' }) : ''}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-[#071B3A] text-base font-semibold">{ev.title}</h3>
                    <div className="flex items-center gap-3 mt-1 text-[#071B3A]/35 text-xs">
                      {ev.location && <span className="flex items-center gap-1"><MapPin size={10} />{ev.location}</span>}
                      <span>{ev.event_type}</span>
                      {ev.host_org && <span>{ev.host_org}</span>}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {ev.registration_link && <a href={ev.registration_link} target="_blank" rel="noopener noreferrer" className="px-3 py-1.5 text-xs font-semibold transition-all" style={{ backgroundColor: '#C99316', color: '#071B3A' }}>Register</a>}
                    <button onClick={() => generateICS(ev)} className="p-1.5 transition-colors text-[#071B3A]/30 hover:text-[#C99316]" title="Add to Calendar"><Download size={14} /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
          {pastEvents.length > 0 && (
            <div className="max-w-4xl mx-auto px-5 sm:px-8 mt-8">
              <p className="text-[11px] uppercase tracking-[0.2em] text-[#071B3A]/25 font-sans mb-3">Past Events</p>
              <div className="space-y-2">
                {pastEvents.slice(0, 5).map((ev) => (
                  <div key={ev.id} className="flex items-center gap-3 px-4 py-2 bg-white/60">
                    <span className="text-[#071B3A]/25 text-xs w-16">{ev.event_date ? new Date(ev.event_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '-'}</span>
                    <span className="text-[#071B3A]/40 text-xs">{ev.title}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>
      )}

      {/* ═══ ENQUIRY FORM ═══ */}
      <section id="enquiry-form" className="py-20" style={{ backgroundColor: '#071B3A' }}>
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <div className="mb-10">
            <p className="text-[11px] uppercase tracking-[0.3em] text-[#C99316]/80 font-sans mb-4">Get In Touch</p>
            <h2 className="font-serif text-2xl sm:text-3xl text-[#FAF8F3] font-light mb-2">Speaking Enquiry</h2>
            <p className="text-[#FAF8F3]/62 text-sm">We'll respond within 2 business days.</p>
          </div>

          <div className="p-6 sm:p-8" style={{ backgroundColor: 'rgba(250,248,243,0.05)', border: '1px solid rgba(201,147,22,0.1)' }}>
            {status === 'success' ? (
              <div className="text-center py-8" data-testid="speaking-success-message">
                <div className="w-14 h-14 flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: 'rgba(34,197,94,0.08)' }}>
                  <CheckCircle className="w-7 h-7 text-green-400" />
                </div>
                <h3 className="font-serif text-xl text-[#FAF8F3] font-light mb-2">Enquiry Sent</h3>
                <p className="text-[#FAF8F3]/60 text-sm mb-4">Confirmation sent. We'll be in touch within 2 business days.</p>
                <button onClick={() => { setStatus('idle'); setFormData({ name: '', email: '', organisation: '', event_type: '', event_date: '', audience_size: '', phone: '', message: '' }); }} className="text-[#C99316] font-medium text-sm transition-colors hover:text-[#F0C654]">
                  Submit another <ArrowRight size={14} className="inline ml-1" />
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-testid="speaking-form">
                {status === 'error' && (
                  <div className="flex items-center gap-3 p-4 mb-6" style={{ backgroundColor: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }} data-testid="speaking-error-message">
                    <AlertCircle size={16} className="text-red-400 flex-shrink-0" />
                    <p className="text-red-300 text-sm">{errorMsg}</p>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-[11px] font-medium text-[#FAF8F3]/52 mb-2 uppercase tracking-wider">Name *</label>
                    <input type="text" name="name" value={formData.name} onChange={handleChange} required disabled={status === 'loading'} data-testid="speaking-name-input" className={inputClass} placeholder="Your full name" />
                  </div>
                  <div>
                    <label className="block text-[11px] font-medium text-[#FAF8F3]/52 mb-2 uppercase tracking-wider">Email *</label>
                    <input type="email" name="email" value={formData.email} onChange={handleChange} required disabled={status === 'loading'} data-testid="speaking-email-input" className={inputClass} placeholder="you@example.com" />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-[11px] font-medium text-[#FAF8F3]/52 mb-2 uppercase tracking-wider">Organisation</label>
                    <input type="text" name="organisation" value={formData.organisation} onChange={handleChange} disabled={status === 'loading'} data-testid="speaking-organisation-input" className={inputClass} placeholder="Your organisation" />
                  </div>
                  <div>
                    <label className="block text-[11px] font-medium text-[#FAF8F3]/52 mb-2 uppercase tracking-wider">Phone</label>
                    <input type="tel" name="phone" value={formData.phone} onChange={handleChange} disabled={status === 'loading'} data-testid="speaking-phone-input" className={inputClass} placeholder="+44 7000 000000" />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div>
                    <label className="block text-[11px] font-medium text-[#FAF8F3]/52 mb-2 uppercase tracking-wider">Event Type</label>
                    <select name="event_type" value={formData.event_type} onChange={handleChange} disabled={status === 'loading'} data-testid="speaking-event-type-select" className={inputClass}>
                      <option value="">Select type</option>
                      <option value="Keynote">Keynote Address</option>
                      <option value="Workshop">Workshop</option>
                      <option value="Executive Retreat">Executive Retreat</option>
                      <option value="Conference">Conference</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-[11px] font-medium text-[#FAF8F3]/52 mb-2 uppercase tracking-wider">Event Date</label>
                    <input type="text" name="event_date" value={formData.event_date} onChange={handleChange} disabled={status === 'loading'} data-testid="speaking-event-date-input" className={inputClass} placeholder="e.g. March 2026" />
                  </div>
                  <div>
                    <label className="block text-[11px] font-medium text-[#FAF8F3]/52 mb-2 uppercase tracking-wider">Audience Size</label>
                    <select name="audience_size" value={formData.audience_size} onChange={handleChange} disabled={status === 'loading'} data-testid="speaking-audience-size-select" className={inputClass}>
                      <option value="">Select size</option>
                      <option value="Under 50">Under 50</option>
                      <option value="50-100">50-100</option>
                      <option value="100-500">100-500</option>
                      <option value="500+">500+</option>
                    </select>
                  </div>
                </div>

                <div className="mb-6">
                  <label className="block text-[11px] font-medium text-[#FAF8F3]/52 mb-2 uppercase tracking-wider">Message *</label>
                  <textarea name="message" value={formData.message} onChange={handleChange} required disabled={status === 'loading'} rows="4" data-testid="speaking-message-input" className={`${inputClass} resize-none`} placeholder="Tell us about your event..." />
                </div>

                <button type="submit" disabled={status === 'loading'} data-testid="speaking-submit-button" className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed" style={{ backgroundColor: '#C99316', color: '#071B3A' }}>
                  {status === 'loading' ? <><Loader2 size={14} className="animate-spin" /><span>Sending...</span></> : <><Send size={14} /><span>Submit Enquiry</span></>}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* ═══ CTA ═══ */}
      <section className="py-16" style={{ backgroundColor: '#FAF8F3' }}>
        <div className="max-w-3xl mx-auto px-5 sm:px-8 text-center">
          <h2 className="font-serif text-2xl sm:text-3xl text-[#071B3A] font-light mb-3">
            Book Promise Monday
          </h2>
          <p className="text-[#071B3A]/45 text-sm mb-8 max-w-md mx-auto">
            Discuss event requirements, availability, and customised content.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <a href="#enquiry-form" className="inline-flex items-center gap-2 px-7 py-3.5 font-sans text-sm font-semibold transition-all duration-300" style={{ backgroundColor: '#071B3A', color: '#FAF8F3' }}>
              <Mail size={16} /> Send Enquiry
            </a>
            <Link to="/about" className="inline-flex items-center gap-2 px-7 py-3.5 font-sans text-sm font-medium transition-all duration-300 text-[#071B3A]/55 hover:text-[#071B3A]" style={{ border: '1px solid rgba(7,27,58,0.15)' }}>
              About Promise Monday <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Speaking;
